import React, { useCallback, useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase-client";
import "./platformFinance.css";
import PlatformFinanceReset from "./PlatformFinanceReset";
import PlatformClubLedger from "./PlatformClubLedger";

const FRESH_FOR_MS = 5 * 60 * 1000;
const money = (amount, currency, factor = 100) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(
    amount / factor,
  );
export function parseReserve(value, factor = 100) {
  const decimals = Math.log10(factor);
  const pattern = decimals
    ? new RegExp(`^\\d+(?:\\.\\d{1,${decimals}})?$`)
    : /^\d+$/;
  if (!pattern.test(value.trim())) return null;
  const [whole, fractional = ""] = value.trim().split(".");
  const amount =
    Number(whole) * factor + Number(fractional.padEnd(decimals, "0"));
  return Number.isSafeInteger(amount) && amount <= 1_000_000_000_000
    ? amount
    : null;
}

export default function PlatformFinancePanel() {
  const [snapshot, setSnapshot] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState("GBP");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [drafts, setDrafts] = useState({});
  const [clock, setClock] = useState(Date.now());
  const inFlight = useRef(false);
  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    try {
      const { data } = await httpsCallable(functions, "getPlatformFinance", {
        timeout: 300000,
      })({});
      setSnapshot(data);
      setClock(Date.now());
      setDrafts((previous) =>
        Object.fromEntries(
          data.currencies.map((row) => [
            row.currency,
            previous[row.currency] ??
              (row.reserve === null
                ? ""
                : String(row.reserve / row.minorUnitFactor)),
          ]),
        ),
      );
    } catch (err) {
      setError(
        err?.message ||
          "Could not reconcile Stripe. Please refresh before withdrawing.",
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, []);
  useEffect(() => {
    refresh();
    const poll = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, FRESH_FOR_MS);
    const tick = setInterval(() => setClock(Date.now()), 15000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  const saveReserve = async (event, row) => {
    event.preventDefault();
    if (inFlight.current) return;
    const amountMinor = parseReserve(
      drafts[row.currency] || "",
      row.minorUnitFactor,
    );
    if (amountMinor === null) {
      setNotice("Enter a valid non-negative reserve amount.");
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    // Invalidate the previous recommendation as soon as settings change.
    setSnapshot((previous) => ({ ...previous, checkedAt: null }));
    try {
      await httpsCallable(
        functions,
        "updatePlatformFinanceReserve",
      )({ currency: row.currency, amountMinor });
      setNotice("Reserve saved.");
    } catch (err) {
      setError(err?.message || "Could not save the reserve.");
      inFlight.current = false;
      setBusy(false);
      return;
    }
    inFlight.current = false;
    await refresh();
  };
  const runResetAction = async (action, reload = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    if (reload) setSnapshot((previous) => ({ ...previous, checkedAt: null }));
    let succeeded = false;
    try {
      const message = await action();
      succeeded = true;
      if (reload) setDrafts({});
      if (message) setNotice(message);
    } catch (err) {
      setError(
        err?.message ||
          "Could not reset historical payments. Preview again before retrying.",
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
    if (reload && succeeded) await refresh();
  };
  const fresh =
    snapshot?.checkedAt &&
    clock - Date.parse(snapshot.checkedAt) < FRESH_FOR_MS;
  const currencies = (snapshot?.currencies || [])
    .filter(
      (row) =>
        row.currency === "GBP" ||
        row.totalBalance !== 0 ||
        row.available !== 0 ||
        row.pending !== 0 ||
        row.stripeReserved > 0 ||
        row.owedToClubs > 0 ||
        row.unmatchedFundsHeld > 0 ||
        row.blockingIssueCount > 0,
    )
    .sort((a, b) =>
      a.currency === "GBP"
        ? -1
        : b.currency === "GBP"
          ? 1
          : a.currency.localeCompare(b.currency),
    );
  const row =
    currencies.find((entry) => entry.currency === selectedCurrency) ||
    currencies[0];
  const format = (amount) => money(amount, row.currency, row.minorUnitFactor);
  const safe =
    row &&
    fresh &&
    !busy &&
    !error &&
    row.safeToWithdraw !== null &&
    (row.blockingIssueCount ?? (snapshot.reconciled ? 0 : 1)) === 0;
  const groups =
    snapshot?.issueGroups ||
    Object.values(
      (snapshot?.issues || []).reduce((result, issue) => {
        const key = `${issue.currency || "all"}:${issue.message}`;
        if (!result[key])
          result[key] = {
            message: issue.message,
            currency: issue.currency,
            severity: issue.severity || "blocking",
            count: 0,
            references: [],
          };
        result[key].count += 1;
        if (issue.reference && result[key].references.length < 5)
          result[key].references.push(issue.reference);
        return result;
      }, {}),
    );
  const visibleGroups = groups.filter(
    (group) => !group.currency || group.currency === row?.currency,
  );
  const checks = visibleGroups.reduce((total, group) => total + group.count, 0);
  const owingClubs = (row?.clubs || [])
    .filter((club) => club.owed > 0)
    .sort((a, b) => b.owed - a.owed);
  return (
    <section
      className="admin-section platform-finance"
      aria-labelledby="platform-finance-title"
    >
      <div className="admin-section__header platform-finance__header">
        <div>
          <h2 id="platform-finance-title">Finance</h2>
          <p>
            Your platform balance, club obligations, and withdrawal allowance.
          </p>
        </div>
        <div className="platform-finance__actions">
          {currencies.length > 1 && (
            <label>
              Currency
              <select
                value={row?.currency || "GBP"}
                onChange={(event) => setSelectedCurrency(event.target.value)}
              >
                {currencies.map((entry) => (
                  <option key={entry.currency} value={entry.currency}>
                    {entry.currency}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="button" onClick={refresh} disabled={busy}>
            {busy ? "Checking Stripe…" : "Refresh from Stripe"}
          </button>
        </div>
      </div>
      <div className="admin-section__body">
        {error && (
          <p role="alert" className="platform-finance__alert">
            {error}
          </p>
        )}
        {notice && <p role="status">{notice}</p>}
        {snapshot && (
          <div className="platform-finance__status">
            <span className="platform-finance__badge">
              {snapshot.livemode ? "Live Stripe account" : "Stripe test mode"}
            </span>
            <span>
              {snapshot.checkedAt
                ? `Checked ${new Date(snapshot.checkedAt).toLocaleString("en-GB")}`
                : "Refresh required"}
            </span>
            <span>Auto-refresh every 5 minutes</span>
          </div>
        )}
        {snapshot?.livemode === false && (
          <p className="platform-finance__muted">
            Test funds only. Stripe test history can outlive development club
            records; unmatched funds are held back in the calculation.
          </p>
        )}
        {snapshot && !fresh && !busy && (
          <p role="status" className="platform-finance__alert">
            Refresh Stripe to get a current withdrawal figure.
          </p>
        )}
        {row && (
          <article
            className="platform-finance__currency"
            aria-label={`${row.currency} finance`}
          >
            <h3 className="platform-finance__currency-title">
              {row.currency} overview
            </h3>
            {row.historicalReset && (
              <p className="platform-finance__muted">
                Historical payments settled on{" "}
                {new Date(row.historicalReset.at).toLocaleString("en-GB")}.{" "}
                Starting available balance:{" "}
                {format(row.historicalReset.openingAvailable)}.{" "}
                {row.historicalReset.cancelledTransfers} old queued transfers
                cancelled. {row.historicalReset.reason}
              </p>
            )}
            <div className="platform-finance__totals">
              <div className="platform-finance__metric">
                <span>Total balance</span>
                <strong>{format(row.totalBalance)}</strong>
                <small>
                  {format(row.available)} available · {format(row.pending)}{" "}
                  settling
                </small>
              </div>
              <div
                className={`platform-finance__metric ${safe ? "platform-finance__metric--safe" : "platform-finance__metric--review"}`}
              >
                <span>Safe to withdraw</span>
                <strong>
                  {safe ? format(row.safeToWithdraw) : "Unavailable"}
                </strong>
                <small>
                  {busy
                    ? "Reconciliation in progress"
                    : !row.reserveConfigured
                      ? "Set a reserve to calculate"
                      : safe
                        ? row.unmatchedFundsHeld > 0
                          ? "After holding back unmatched funds"
                          : "After protecting club money and your reserve"
                        : "A fresh check and resolution of blocking issues are required"}
                </small>
              </div>
            </div>
            {!safe && !busy && fresh && row.reserveConfigured && !error && (
              <p className="platform-finance__alert" role="status">
                <strong>Withdrawal recommendation unavailable</strong> · Review
                the blocking checks below.
              </p>
            )}
            <div className="platform-finance__workspace">
              <section
                className="platform-finance__panel"
                aria-label="Withdrawal calculation"
              >
                <h4>Calculation breakdown</h4>
                <dl className="platform-finance__breakdown">
                  <div>
                    <dt>Total Stripe balance</dt>
                    <dd>{format(row.totalBalance)}</dd>
                  </div>
                  <div>
                    <dt>Less funds still settling</dt>
                    <dd>− {format(row.pending)}</dd>
                  </div>
                  <div className="platform-finance__subtotal">
                    <dt>Available balance</dt>
                    <dd>{format(row.available)}</dd>
                  </div>
                  <div>
                    <dt>Less money owed to clubs</dt>
                    <dd>− {format(row.owedToClubs)}</dd>
                  </div>
                  {row.unmatchedFundsHeld > 0 && (
                    <div>
                      <dt>Less unmatched funds held for review</dt>
                      <dd>− {format(row.unmatchedFundsHeld)}</dd>
                    </div>
                  )}
                  <div>
                    <dt>Less your reserve</dt>
                    <dd>
                      {row.reserve === null
                        ? "Not set"
                        : `− ${format(row.reserve)}`}
                    </dd>
                  </div>
                  {row.withdrawalRounding > 0 && (
                    <div>
                      <dt>Less payout currency rounding</dt>
                      <dd>− {format(row.withdrawalRounding)}</dd>
                    </div>
                  )}
                  <div className="platform-finance__result">
                    <dt>Safe to withdraw</dt>
                    <dd>{safe ? format(row.safeToWithdraw) : "Unavailable"}</dd>
                  </div>
                </dl>
                {row.shortfall > 0 && (
                  <p className="platform-finance__muted">
                    {format(row.shortfall)} more in settled funds is needed to
                    cover club money, any unmatched funds, and your reserve. The
                    withdrawal allowance is floored at zero.
                  </p>
                )}
                {row.unmatchedFundsHeld > 0 && (
                  <p className="platform-finance__muted">
                    Unmatched payments are retained conservatively until their
                    ownership can be verified. They are not treated as platform
                    income.
                  </p>
                )}
              </section>
              <section
                className="platform-finance__panel platform-finance__panel--reserve"
                aria-label="Withdrawal reserve"
              >
                <h4>Your reserve</h4>
                <p className="platform-finance__muted">
                  Keep extra funds for refunds, disputes, and unexpected costs.
                </p>
                <form
                  className="platform-finance__reserve"
                  onSubmit={(event) => saveReserve(event, row)}
                >
                  <label htmlFor={`reserve-${row.currency}`}>
                    Reserve amount ({row.currency})
                    <input
                      id={`reserve-${row.currency}`}
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      value={drafts[row.currency] || ""}
                      disabled={busy}
                      onChange={(event) =>
                        setDrafts((previous) => ({
                          ...previous,
                          [row.currency]: event.target.value,
                        }))
                      }
                      aria-describedby={`reserve-help-${row.currency}`}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={
                      busy ||
                      parseReserve(
                        drafts[row.currency] || "",
                        row.minorUnitFactor,
                      ) === null
                    }
                  >
                    Save reserve
                  </button>
                  <p id={`reserve-help-${row.currency}`}>
                    Club money and unmatched funds are protected separately.
                    Zero is allowed.
                  </p>
                </form>
              </section>
            </div>
            {row.alerts?.length > 0 && (
              <details
                className="platform-finance__details"
                aria-label="Finance alerts"
              >
                <summary>
                  Finance alerts{" "}
                  <span>{row.alerts.length} items need attention</span>
                </summary>
                <ul className="platform-finance__details-body platform-finance__alerts">
                  {row.alerts.slice(0, 50).map((alert, index) => (
                    <li key={index}>
                      <strong>
                        {alert.club}
                        {alert.label ? ` · ${alert.label}` : ""}
                      </strong>
                      <p>
                        {alert.message}
                        {alert.amount ? ` ${format(alert.amount)}` : ""}
                      </p>
                    </li>
                  ))}
                  {row.alerts.length > 50 && (
                    <li>
                      Showing the first 50 alerts. Use the club ledger to review
                      all payments.
                    </li>
                  )}
                </ul>
              </details>
            )}
            <PlatformClubLedger
              key={`ledger-${row.currency}`}
              row={row}
              busy={busy}
              run={runResetAction}
              format={format}
              parseAmount={parseReserve}
            />
            <PlatformFinanceReset
              key={row.currency}
              row={row}
              busy={busy}
              run={runResetAction}
              parseAmount={parseReserve}
              format={format}
            />
            {checks > 0 && (
              <details
                className="platform-finance__details platform-finance__review"
                aria-label="Reconciliation review"
              >
                <summary>
                  Reconciliation review{" "}
                  <span>
                    {checks} {checks === 1 ? "check" : "checks"} ·{" "}
                    {visibleGroups.length}{" "}
                    {visibleGroups.length === 1 ? "group" : "groups"}
                  </span>
                </summary>
                <div className="platform-finance__details-body">
                  <p className="platform-finance__muted">
                    Held-funds checks have already been accounted for in the
                    calculation. Blocking checks withhold the withdrawal
                    recommendation for this currency.
                  </p>
                  {visibleGroups.map((group, index) => (
                    <details
                      className="platform-finance__issue-group"
                      key={index}
                    >
                      <summary>
                        <span
                          className={`platform-finance__badge ${group.severity === "review" ? "" : "platform-finance__badge--blocking"}`}
                        >
                          {group.severity === "review"
                            ? "Protected / review"
                            : "Blocking"}
                        </span>
                        <span>{group.message}</span>
                        <strong>{group.count}</strong>
                      </summary>
                      <div className="platform-finance__issue-samples">
                        <p>
                          {group.currency || "All currencies"} · Showing up to
                          five sample references.
                        </p>
                        <ul>
                          {group.references.map((reference, sampleIndex) => (
                            <li key={sampleIndex}>
                              <code>{reference}</code>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </details>
                  ))}
                  {!snapshot.issueGroups &&
                    snapshot.issueCount > snapshot.issues.length && (
                      <p className="platform-finance__muted">
                        Summary based on the first {snapshot.issues.length}{" "}
                        checks. Refresh after deploying the updated Finance
                        function for complete grouped counts.
                      </p>
                    )}
                </div>
              </details>
            )}
            <details className="platform-finance__details">
              <summary>
                Club obligations{" "}
                <span>
                  {format(row.owedToClubs)} owed
                  {owingClubs.length > 0 ? ` · ${owingClubs.length} clubs` : ""}
                </span>
              </summary>
              <div className="platform-finance__details-body">
                <dl className="platform-finance__breakdown">
                  <div>
                    <dt>Waiting for release or paused for review</dt>
                    <dd>{format(row.waiting)}</dd>
                  </div>
                  <div>
                    <dt>Pending transfers</dt>
                    <dd>{format(row.pendingTransfers)}</dd>
                  </div>
                  <div>
                    <dt>Failed transfers</dt>
                    <dd>{format(row.failedTransfers)}</dd>
                  </div>
                  <div>
                    <dt>Owed without an active instruction</dt>
                    <dd>{format(row.missingTransfers)}</dd>
                  </div>
                </dl>
                {owingClubs.length > 0 && (
                  <ul className="platform-finance__clubs">
                    {owingClubs.map((club, index) => (
                      <li key={index}>
                        <span>{club.name}</span>
                        <strong>{format(club.owed)} owed</strong>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="platform-finance__muted">
                  Already transferred to clubs (net of reversals):{" "}
                  {format(row.transferredToClubs)}. These funds have left the
                  platform balance and are not deducted again.
                </p>
                {row.stripeReserved > 0 && (
                  <p className="platform-finance__muted">
                    Stripe Connect reserve: {format(row.stripeReserved)}.
                    Separate from the available and pending balance.
                  </p>
                )}
              </div>
            </details>
          </article>
        )}
        {snapshot && !row && (
          <p>No active currency balances were returned by Stripe.</p>
        )}
        {snapshot?.recentActions?.length > 0 && (
          <details
            className="platform-finance__details"
            aria-label="Financial action history"
          >
            <summary>Recent financial actions</summary>
            <ul className="platform-finance__details-body platform-finance__alerts">
              {snapshot.recentActions.map((action) => (
                <li key={action.id}>
                  <strong>
                    {{
                      recover: "Stalled transfer reviewed",
                      pause: "Transfer paused",
                      resume: "Transfer resumed",
                      cancel: "Automatic transfer stopped",
                      adjust: "Agreed amount recorded",
                      settle: "Settled using club overpayment",
                    }[action.action] || "Financial action recorded"}
                    {action.at
                      ? ` · ${new Date(action.at).toLocaleString("en-GB")}`
                      : ""}
                  </strong>
                  <p>{action.note}</p>
                </li>
              ))}
            </ul>
          </details>
        )}
        {snapshot?.recentFees?.length > 0 && (
          <details
            className="platform-finance__details"
            aria-label="Stripe account fees"
          >
            <summary>Recent Stripe account fees</summary>
            <div className="platform-finance__details-body">
              <p>
                These account and payout fees are already deducted from Stripe’s
                balance, separately from card-processing fees.
              </p>
              <ul className="platform-finance__clubs">
                {snapshot.recentFees.map((fee) => (
                  <li key={fee.id}>
                    <span>
                      {new Date(fee.at).toLocaleDateString("en-GB")} ·{" "}
                      {fee.description}
                    </span>
                    <strong>
                      {money(fee.amount, fee.currency, fee.minorUnitFactor)}
                    </strong>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        )}
        {snapshot?.recentWithdrawals?.length > 0 && (
          <details
            className="platform-finance__details"
            aria-label="Club obligations"
          >
            <summary>
              Recent platform withdrawals{" "}
              <span>Latest {snapshot.recentWithdrawals.length}</span>
            </summary>
            <div className="platform-finance__details-body">
              <ul className="platform-finance__clubs">
                {snapshot.recentWithdrawals.map((payout) => (
                  <li key={payout.id}>
                    <span>
                      {new Date(payout.createdAt).toLocaleDateString("en-GB")} ·{" "}
                      {payout.status.replace(/_/g, " ")}
                    </span>
                    <strong>
                      {money(
                        payout.amount,
                        payout.currency,
                        payout.minorUnitFactor,
                      )}
                    </strong>
                  </li>
                ))}
              </ul>
              <p className="platform-finance__muted">
                These withdrawals are already reflected in Stripe’s balance.
              </p>
            </div>
          </details>
        )}
        <p className="platform-finance__muted platform-finance__footnote">
          Refresh immediately before withdrawing in Stripe. New transactions can
          change this figure, and future refunds or disputes can exceed your
          reserve.
        </p>
      </div>
    </section>
  );
}
